/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
    HubConnectionBuilder,
    HubConnection,
    HubConnectionState,
    LogLevel
} from "@microsoft/signalr";
import without from "lodash/without";
import { v4 as uuidv4 } from "uuid";
import { LegacyTimelineDataStore } from "../loggedInUser/LegacyTimelineDataStore";
import { when } from "mobx";
import { Container } from "../injection";
import { locationPrepend } from "../devServer/locationPrepend";
import { windowAsAny } from "../windowAsAny/windowAsAny";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericType = any;

type ErrorType = "server" | "network" | "timeout" | null;
export type ConnectionStatus =
    | "disconnected"
    | "connecting"
    | "reconnecting"
    | "connected";

type IDictionary<T> = Record<string, T>;

interface IStatus {
    status: ConnectionStatus;
    isInvalidated: boolean;
    attempConnectionDelay: number;
}

interface IState {
    isStarted: boolean;
    status: ConnectionStatus;
    isInvalidated: boolean;
    connection: HubConnection | null;
    lastConnectionTime: number | null;
    lastDisconnectedTime: number | null;
    attemptConnectionCount: number;
    attemptConnectionTimeout: NodeJS.Timeout | null;
    requestId: number;
    requests: IDictionary<IRequest>;
    subscriptionId: number;
    subscriptions: IDictionary<ISubscription>;
    subscriptionUuids: IDictionary<string>;
    recentMessages: IDictionary<Date>;
    recentMessageTypes: IDictionary<Date[]>;
    statusSubscriptions: readonly (() => void)[];
    timeoutNoticeSubscriptions: readonly ((
        requests: ITimedOutRequest[]
    ) => void)[];
}

interface IRequestReferer {
    referer?: string;
}

interface IRequestMessage {
    message: RedDeer.Contracts.Base.Message | string;
}

interface IRequest extends IRequestReferer, IRequestMessage {
    status: "created" | "sent";
    type:
        | "send"
        | "asyncSend"
        | "sendAll"
        | "sendToHandler"
        | "sendRemote"
        | "subscribe"
        | "asyncSubscribe"
        | "unsubscribe";
    handlerId?: string;
    service?: string;
    message: RedDeer.Contracts.Base.Message | string; //  request "unsubscribe" uses message field to send payload
    responseCallback?: (response: GenericType) => void;
    errorCallback?: (error: GenericType, type: ErrorType) => void;
    currentOperation: string;
    finallyOnce: () => void;
    timeoutInMs?: number;
    timeoutCallback?: () => void;
    signalRMessage?: GenericType;
    pokeTimeout?: NodeJS.Timeout | null;
    lastSentTime?: number;
}

interface ISubscription extends IRequestReferer, IRequestMessage {
    status:
        | "created"
        | "sent"
        | "active"
        | "sendNetworkFailed"
        | "disconnected";
    type: "subscribe" | "asyncSubscribe";
    message: RedDeer.Contracts.Base.Message | string; //  request "unsubscribe" uses message field to send payload;
    responseCallback: (
        response: RedDeer.Contracts.Base.CallbackResponse
    ) => void;
    currentOperation: string;
    finallyOnce: () => void;
    uuid?: string;
    timeoutInMs?: number;
    timeoutCallback?: () => void;
}

export interface ITimedOutRequest {
    id: string;
    currentOperation: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const $: any; // JQuery

const defaultTimeoutInMs: number = 2 * 60 * 1000;

export class MessageBusClient {
    private static instance: MessageBusClient | null = null;

    public static getInstance(container: Container): MessageBusClient {
        // already have instance
        if (this.instance !== null) {
            return this.instance;
        }

        // create
        this.instance = new MessageBusClient(container);

        // set location for message bus callbacks
        const w = windowAsAny();
        w.RedDeer ??= {};
        w.RedDeer.UI ??= {};
        w.RedDeer.UI.MessageBusClient ??= {};
        w.RedDeer.UI.MessageBusClient.instance = this.instance;

        return this.instance;
    }

    public static getConnectedInstance(container: Container): MessageBusClient {
        const instance = this.getInstance(container);
        if (!instance.state.isStarted) {
            instance.state.isStarted = true;
            instance.start();
        }
        return instance;
    }

    private state: IState;

    /** passing Container is bad practise. but it is needed here to avoid a circular dependency on LegacyUserDetailsStore */
    constructor(private container: Container) {
        this.state = {
            isStarted: false,
            status: "disconnected",
            isInvalidated: false,
            connection: null,
            lastConnectionTime: null,
            lastDisconnectedTime: null,
            attemptConnectionCount: 0,
            attemptConnectionTimeout: null,
            requestId: 0,
            requests: {},
            subscriptionId: 0,
            subscriptions: {},
            subscriptionUuids: {},
            recentMessages: {},
            recentMessageTypes: {},
            statusSubscriptions: [],
            timeoutNoticeSubscriptions: []
        };
        setInterval(this.backgroundInterval.bind(this), 1000);
    }

    private start(): void {
        this.createConnection();
        this.attemptConnection();
        void this.startCheckingIfLoggedIn();
    }

    public invalidateSession(isImpersonationChange?: boolean) {
        // kill any subscriptions that will otherwise keep on going with an invalid session.
        window.ErrorHandler.messageBusConnectionManuallyKilled();

        this.state.isInvalidated = true;
        void this.state.connection?.stop();

        if (isImpersonationChange) {
            location.reload();
        } else {
            location.href = `${locationPrepend()}/Security/Login?msg=SessionTimeout`;
        }

        this.notifyStatusChange();
    }

    public subscribeToStatus(callback: () => void): () => void {
        this.state.statusSubscriptions = [
            ...this.state.statusSubscriptions,
            callback
        ];
        return () => {
            this.state.statusSubscriptions = without(
                this.state.statusSubscriptions,
                callback
            );
        };
    }

    public status(): IStatus {
        return {
            status: this.state.status,
            isInvalidated: this.state.isInvalidated,
            attempConnectionDelay: this.attempConnectionDelay()
        };
    }

    public subscribeToTimeoutNotices(
        callback: (requests: ITimedOutRequest[]) => void
    ): () => void {
        this.state.timeoutNoticeSubscriptions = [
            ...this.state.timeoutNoticeSubscriptions,
            callback
        ];
        return () => {
            this.state.timeoutNoticeSubscriptions = without(
                this.state.timeoutNoticeSubscriptions,
                callback
            );
        };
    }

    public killRequestTimeouts(ids: string[]): void {
        for (const id of ids) {
            this.requestReceived(id, "Request timeout limit exceed", "timeout");
        }
        this.backgroundInterval(); // run background interval so that the timeout notice is removed immediately
    }

    public resetRequestTimeouts(ids: string[]): void {
        for (const id of ids) {
            const request = this.state.requests[id];
            if (request) {
                request.lastSentTime = Date.now();
            }
        }
        this.backgroundInterval(); // run background interval so that the timeout notice is removed immediately
    }

    public attemptConnectionNow(): void {
        this.state.attemptConnectionCount = 0;
        this.attemptConnection();
    }

    public send(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback?: (response: GenericType) => GenericType,
        finallyCallback?: () => GenericType,
        errorCallback?: (error: GenericType, type: ErrorType) => void,
        timeoutInMs?: number
    ): void {
        this.createRequest({
            status: "created",
            type: "send",
            message,
            responseCallback,
            currentOperation,
            finallyOnce: this.createFinallyOnce(finallyCallback),
            errorCallback,
            timeoutInMs
        });
        this.sendWaiting();
    }

    public asyncSend(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback?: (response: GenericType) => GenericType,
        finallyCallback?: () => GenericType,
        errorCallback?: (error: GenericType, type: ErrorType) => void,
        timeoutInMs?: number
    ): void {
        this.createRequest({
            status: "created",
            type: "asyncSend",
            message,
            responseCallback,
            currentOperation,
            finallyOnce: this.createFinallyOnce(finallyCallback),
            errorCallback,
            timeoutInMs
        });
        this.sendWaiting();
    }

    public asyncSendAjax(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback?: (response: GenericType) => GenericType,
        finallyCallback?: () => GenericType,
        errorCallback?: (error: GenericType /* JQueryXHR */) => GenericType
    ): GenericType /* JQueryXHR */ {
        const finallyOnce = this.createFinallyOnce(finallyCallback);
        // NOTE: legacy support: this function assumes that jQuery is available, which will only be the case if the angular codebase has been loaded
        this.attachMessageId(message);
        const requestUrl = window.location.href;
        return $.ajax({
            type: "POST",
            url: `${locationPrepend()}/MessageBus/AsyncSendAjax`,
            contentType: "application/json",
            data: JSON.stringify(message),
            dataType: "json",
            xhrFields: {
                withCredentials: true
            },
            success: (
                response: GenericType /* could be inferred from jQuery */
            ) => {
                if (responseCallback) {
                    responseCallback(response);
                }
                finallyOnce();
            },
            error: (err: GenericType /* could be inferred from jQuery */) => {
                if (err.statusText !== "abort") {
                    if (errorCallback) {
                        errorCallback(err);
                    } else {
                        this.triggerError(
                            err.responseText ?? "An error has occurred.",
                            currentOperation,
                            false,
                            message,
                            requestUrl
                        );
                    }
                }
                finallyOnce();
            }
        });
    }

    public sendAjax(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback?: (response: GenericType) => GenericType,
        errorCallback?: (error: GenericType /* JQueryXHR */) => GenericType
    ): GenericType /* JQueryXHR */ {
        // NOTE: legacy support: this function assumes that jQuery is available, which will only be the case if the angular codebase has been loaded
        this.attachMessageId(message);
        const requestUrl = window.location.href;
        return $.ajax({
            type: "POST",
            url: `${locationPrepend()}/MessageBus/SendAjax`,
            contentType: "application/json",
            data: JSON.stringify(message),
            dataType: "json",
            xhrFields: {
                withCredentials: true
            },
            success: (
                response: GenericType /* could be inferred from jQuery */
            ) => {
                if (responseCallback) {
                    responseCallback(response);
                }
            },
            error: (err: GenericType /* could be inferred from jQuery */) => {
                if (errorCallback) {
                    errorCallback(err);
                } else {
                    this.triggerError(
                        err,
                        currentOperation,
                        false,
                        message,
                        requestUrl
                    );
                }
            }
        });
    }

    public sendAll(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback: (response: GenericType[]) => GenericType
    ): void {
        this.createRequest({
            status: "created",
            type: "sendAll",
            message,
            responseCallback,
            currentOperation,
            finallyOnce: () => {
                /* do nothing */
            }
        });
        this.sendWaiting();
    }

    public sendToHandler(
        message: RedDeer.Contracts.Base.Message,
        handlerId: string,
        currentOperation: string,
        responseCallback?: (response: GenericType) => GenericType,
        finallyCallback?: () => GenericType,
        errorCallback?: (error: GenericType, type: ErrorType) => void
    ): void {
        this.createRequest({
            status: "created",
            type: "sendToHandler",
            handlerId,
            message,
            responseCallback,
            currentOperation,
            finallyOnce: this.createFinallyOnce(finallyCallback),
            errorCallback
        });
        this.sendWaiting();
    }

    public sendRemote(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        service: string,
        responseCallback?: (response: GenericType[]) => GenericType,
        finallyCallback?: () => GenericType
    ): void {
        this.createRequest({
            status: "created",
            type: "sendRemote",
            service,
            message,
            currentOperation,
            responseCallback,
            finallyOnce: this.createFinallyOnce(finallyCallback)
        });
        this.sendWaiting();
    }

    public subscribe(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback: (
            response: RedDeer.Contracts.Base.CallbackResponse
        ) => void,
        finallyCallback?: () => void,
        timeoutInMs?: number,
        timeoutCallback?: () => void
    ): () => void {
        const cancel = this.createSubscription({
            status: "created",
            type: "subscribe",
            message,
            responseCallback,
            currentOperation,
            finallyOnce: this.createFinallyOnce(finallyCallback),
            timeoutInMs,
            timeoutCallback
        });
        this.sendWaiting();
        return cancel;
    }

    public asyncSubscribe(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCallback: (
            response: RedDeer.Contracts.Base.CallbackResponse
        ) => void,
        finallyCallback?: () => void,
        timeoutInMs?: number,
        timeoutCallback?: () => void
    ): () => void {
        const cancel = this.createSubscription({
            status: "created",
            type: "asyncSubscribe",
            message,
            responseCallback,
            currentOperation,
            finallyOnce: this.createFinallyOnce(finallyCallback),
            timeoutInMs,
            timeoutCallback
        });
        this.sendWaiting();
        return cancel;
    }

    public asyncProgressSubscription(
        message: RedDeer.Contracts.Base.Message,
        currentOperation: string,
        responseCompleteCallback: (
            response: RedDeer.Contracts.Base.ProgressUpdateResponse
        ) => void,
        errorCallback: (
            errors: RedDeer.Contracts.Base.ValidationError[]
        ) => void,
        finallyCallback?: () => void,
        progressUpdateCallback?: (progress: number, message: string) => void
    ) {
        const endSubscription = this.asyncSubscribe(
            message,
            currentOperation,
            (
                response: GenericType // RedDeer.Contracts.Commands.Mifid.ProgressValuationResponse
            ) => {
                if (response.Errors) {
                    errorCallback(response.Errors);
                } else {
                    if (response.IsComplete) {
                        responseCompleteCallback(response);
                    } else {
                        if (progressUpdateCallback) {
                            progressUpdateCallback(
                                response.Progress,
                                response.StatusMessage
                            );
                        } else {
                            // eslint-disable-next-line no-console
                            console.log(
                                response.Progress,
                                response.StatusMessage
                            );
                        }
                    }
                }

                if (response.Errors || response.IsComplete) {
                    endSubscription();

                    if (finallyCallback) {
                        finallyCallback();
                    }
                }
            }
        );
    }

    private createFinallyOnce(finallyCallback?: () => void): () => void {
        const finallyOnce = finallyCallback
            ? onceOnly(finallyCallback)
            : () => {
                  /* do nothing */
              };
        return finallyOnce;
    }

    private createConnection(): void {
        if (this.state.connection) {
            throw new Error("Connection already exists");
        }

        this.state.connection = new HubConnectionBuilder()
            .configureLogging(LogLevel.Information)
            .withUrl(`${locationPrepend()}/messageBusHubAspNetCore`)
            .build();

        const onConnected = () => {
            if (this.state.connection?.state !== HubConnectionState.Connected) {
                this.log(
                    "message bus not yet connected, even though we have a Connected callback",
                    this.state.connection?.state
                );
                if (
                    this.state.connection?.state ===
                    HubConnectionState.Connecting
                ) {
                    this.log("will try again after a timeout");
                    setTimeout(() => {
                        onConnected();
                    });
                }
                return;
            }

            this.log("message bus connected", this.state.connection.state);

            this.state.status = "connected";
            this.state.lastConnectionTime = Date.now();
            this.state.attemptConnectionCount = 0;
            this.sendWaiting();

            this.notifyStatusChange();
        };
        this.state.connection.on("Connected", _ => {
            onConnected();
        });

        this.state.connection.on("Response", response => {
            try {
                this.log("response received", response);
                const responseId = response.i;
                const responseType = response.t;
                const responseBody = response.b;

                switch (responseType) {
                    case RedDeer.Contracts.Dtos.PersistentConnections
                        .PersistentConnectionResponseType.ResponseSuccess:
                        this.requestReceived(responseId, responseBody, null);
                        break;
                    case RedDeer.Contracts.Dtos.PersistentConnections
                        .PersistentConnectionResponseType.ResponseError:
                        this.requestReceived(
                            responseId,
                            responseBody,
                            "server"
                        );
                        break;
                    case RedDeer.Contracts.Dtos.PersistentConnections
                        .PersistentConnectionResponseType.Callback:
                        this.subscriptionReceived(responseBody);
                        break;
                }
            } catch (err) {
                const responseId = response.i;

                const request = this.state.requests[responseId];

                this.triggerError(
                    err as Error,
                    "Message bus receive handler",
                    false,
                    request?.message,
                    request?.referer
                );
            }
        });

        this.state.connection.on("Logout", () => {
            this.invalidateSession();
        });

        // disconnected
        this.state.connection.onclose((error?: Error) => {
            this.log("Message bus disconnected", error);

            this.state.status = "disconnected";

            const reason = error?.message
                ? `Message bus disconnected caused request error: ${error.message}`
                : "Message bus disconnected caused request error";

            setTimeout(() => {
                // disconnect happens before window.unload (without a setTimeout, unneeded error messages are logged)
                this.disconnectRequestsAndSubscriptions(reason);
            });

            this.state.lastDisconnectedTime = Date.now();
            this.attemptConnection();

            this.notifyStatusChange();
        });
    }

    private disconnectRequestsAndSubscriptions(reason: string): void {
        for (const id in this.state.requests) {
            const request = this.state.requests[id];
            if (request?.status === "sent") {
                this.requestReceived(id, reason, "network");
            }
        }

        for (const id in this.state.subscriptions) {
            const subscription = this.state.subscriptions[id];
            if (
                subscription?.status === "sent" ||
                subscription?.status === "active"
            ) {
                subscription.status = "disconnected"; // will cause retry on next connection
            }
        }
    }

    private startConnection(): void {
        if (this.state.status !== "disconnected") {
            this.error("Cannot start an un-disconnected connection");
            return;
        }

        this.state.connection?.start().then(
            _ => {
                this.log(
                    "message bus connection started",
                    this.state.connection?.state
                );
                // Logic moved to "Connected" callback
            },
            (_: unknown) => {
                this.log(
                    "message bus connection error",
                    this.state.connection?.state
                );

                this.state.status = "disconnected";
                this.state.lastDisconnectedTime = Date.now();
                this.state.attemptConnectionCount++;
                this.attemptConnection();

                this.notifyStatusChange();
            }
        );
        this.state.status = "connecting";

        this.notifyStatusChange();
    }

    private attemptConnection(): void {
        if (this.state.attemptConnectionTimeout) {
            clearTimeout(this.state.attemptConnectionTimeout);
            this.state.attemptConnectionTimeout = null;
        }

        if (this.state.status !== "disconnected") {
            this.error(
                "Cannot attempt connection on an un-disconnected connection"
            );
            return;
        }

        const delay = this.attempConnectionDelay();
        this.log(
            "attempting message bus connection in " + delay.toString() + "ms"
        );

        this.state.attemptConnectionTimeout = setTimeout(() => {
            this.state.attemptConnectionTimeout = null;
            this.startConnection();
        }, delay);
    }

    private attempConnectionDelay(): number {
        const max = 30000;
        let addWait = Math.pow(2, this.state.attemptConnectionCount) * 1000;
        if (addWait > max) {
            addWait = max;
        }
        let delay =
            (this.state.lastDisconnectedTime ?? 0) + addWait - Date.now();
        if (delay < 0) {
            delay = 0;
        }
        if (delay > max) {
            delay = max;
        }
        return delay;
    }

    private createRequest(request: IRequest): void {
        this.state.requestId++;
        const id = this.state.requestId;
        this.state.requests[id] = request;
        this.attachReferer(request);
        this.attachMessageId(request.message);
        this.detectDuplicateMessages(request.message);
        this.log("request", id, request);
    }

    private createSubscription(subscription: ISubscription): () => void {
        this.state.subscriptionId++;
        const id = this.state.subscriptionId;
        this.state.subscriptions[id] = subscription;
        this.attachReferer(subscription);
        this.log("subscription", id, subscription);
        return () => {
            this.cancelSubscription(id);
        };
    }

    private sendWaiting(): void {
        if (this.state.status !== "connected") {
            return;
        }

        this.sendWaitingSubscriptions();
        this.sendWaitingRequests();
    }

    private sendWaitingSubscriptions(): void {
        for (const id in this.state.subscriptions) {
            const subscription = this.state.subscriptions[id];

            const unsubscribeOldUuid = () => {
                if (subscription?.uuid) {
                    this.createRequest({
                        status: "created",
                        type: "unsubscribe",
                        message: subscription.uuid as GenericType,
                        responseCallback: _ => {
                            // do nothing with response
                        },
                        errorCallback: _ => {
                            // do nothing with error
                        },
                        currentOperation: "Unsubscribe from old uuid",
                        finallyOnce: () => {
                            /* do nothing */
                        },
                        timeoutCallback: () => {
                            /* do nothing with timeout*/
                        }
                    });
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete this.state.subscriptionUuids[subscription.uuid];
                }
            };

            if (
                subscription?.status === "created" ||
                subscription?.status === "sendNetworkFailed" ||
                subscription?.status === "disconnected"
            ) {
                // remove old uuid
                unsubscribeOldUuid();

                // update uuid
                subscription.uuid = uuidv4();
                if (typeof subscription.message === "object") {
                    subscription.message.MessageId = subscription.uuid;
                }
                this.state.subscriptionUuids[subscription.uuid] = id;

                this.createRequest({
                    status: "created",
                    type: subscription.type,
                    message: subscription.message,
                    referer: subscription.referer,
                    responseCallback: _ => {
                        // NB: do nothing with actual response
                        this.log("subscription request successful", id);
                        subscription.status = "active";
                    },
                    errorCallback: (error, type) => {
                        if (!this.state.subscriptions[id]) {
                            // subscription already removed (either cancelled or already errored)
                            this.error("Cannot find subscription to error", id);
                            return;
                        }
                        if (type === "network") {
                            subscription.status = "sendNetworkFailed";
                            this.sendWaiting();
                        } else {
                            if (type === "timeout") {
                                unsubscribeOldUuid();
                            }

                            this.subscriptionError(id, new Error(error), type);

                            subscription.finallyOnce();
                        }
                    },
                    currentOperation: subscription.currentOperation,
                    finallyOnce: () => {
                        /* do nothing */
                    },
                    timeoutInMs: subscription.timeoutInMs,
                    timeoutCallback:
                        subscription.timeoutCallback &&
                        (() => {
                            try {
                                subscription.timeoutCallback?.();
                                this.removeSubscription(id);
                            } finally {
                                subscription.finallyOnce();
                            }
                        })
                });

                this.log("sent subscription", id);
                subscription.status = "sent";
            }
        }
    }

    private sendWaitingRequests(): void {
        for (const id in this.state.requests) {
            const request = this.state.requests[id];

            if (request?.status === "created") {
                let type:
                    | RedDeer.Contracts.Dtos.PersistentConnections.PersistentConnectionRequestType
                    | undefined;
                if (request.type === "send") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.Send;
                }
                if (request.type === "asyncSend") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.SendAsync;
                }
                if (request.type === "sendAll") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.SendAll;
                }
                if (request.type === "sendToHandler") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.SendToHandler;
                }
                if (request.type === "sendRemote") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.SendRemote;
                }
                if (request.type === "subscribe") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.Subscribe;
                }
                if (request.type === "asyncSubscribe") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.SubscribeAsync;
                }
                if (request.type === "unsubscribe") {
                    type =
                        RedDeer.Contracts.Dtos.PersistentConnections
                            .PersistentConnectionRequestType.Unsubscribe;
                }

                const requestBody = {
                    i: id,
                    t: type,
                    b: request.message,
                    d: request.currentOperation,
                    h: undefined as unknown as string,
                    s: undefined as unknown as string
                };
                if (request.handlerId) {
                    requestBody.h = request.handlerId;
                }
                if (request.service) {
                    requestBody.s = request.service;
                }
                request.signalRMessage = requestBody;

                if (
                    this.state.connection?.state ===
                    HubConnectionState.Connected
                ) {
                    (
                        this.state.connection.send(
                            "Request",
                            request.signalRMessage
                        ) as Promise<GenericType>
                    ).catch((reason: unknown) => {
                        this.requestReceived(
                            id,
                            "Message bus request send failed: " +
                                (typeof reason === "string"
                                    ? reason.toString()
                                    : ""),
                            "network"
                        );
                    });

                    this.log("sent request", id);
                    request.status = "sent";
                    request.lastSentTime = Date.now();

                    // poke interval
                    if (request.pokeTimeout) {
                        clearTimeout(request.pokeTimeout);
                    }

                    if (typeof request.message === "string") {
                        continue;
                    }
                    const messageType = request.message.MessageType;
                    if (messageType) {
                        request.pokeTimeout = setTimeout(() => {
                            this.poke(messageType);
                        }, 2000);
                    }
                } else {
                    this.log(
                        "message bus not connected",
                        this.state.connection?.state
                    );

                    this.state.status = "disconnected";
                    this.state.lastDisconnectedTime = Date.now();
                    this.state.attemptConnectionCount++;
                    this.attemptConnection();

                    this.notifyStatusChange();

                    break;
                }
            }
        }
    }

    private poke(message: string): void {
        if (this.state.status === "connected") {
            const id = ++this.state.requestId;
            const requestBody = {
                i: id,
                t: RedDeer.Contracts.Dtos.PersistentConnections
                    .PersistentConnectionRequestType.Poke,
                b: message
            };
            const signalRMessage = requestBody;
            if (this.state.connection?.state === HubConnectionState.Connected) {
                void this.state.connection.send("Request", signalRMessage);
            }
            this.log("poke", signalRMessage);
        }
    }

    private removeRequest(
        id: string,
        processRequest: (request: IRequest) => void
    ): void {
        const request = this.state.requests[id];

        if (!request) {
            this.error("Request received: Cannot find request ", id);
            return;
        }

        if (request.pokeTimeout) {
            clearTimeout(request.pokeTimeout);
            request.pokeTimeout = undefined;
        }

        try {
            processRequest(request);
        } finally {
            request.finallyOnce();
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.state.requests[id];
        }

        if (this.hasRequestTimedOut(request)) {
            this.backgroundInterval(); // run background interval so that the timeout notice is removed immediately
        }
    }

    private requestReceived(
        id: string,
        response: GenericType,
        errorType: ErrorType
    ): void {
        this.removeRequest(id, request => {
            try {
                if (errorType) {
                    this.log("request errored", id);
                    if (request.errorCallback) {
                        const error = new Error(
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            `${response} (${request.currentOperation})`
                        );
                        (error as GenericType).__isTimeout =
                            errorType === "timeout"; // add property to error so we can detect a timeouts in window.onunhandledrejection
                        request.errorCallback(error, errorType);
                    } else {
                        throw new Error(response);
                    }
                } else {
                    this.log("request received", id);
                    if (request.responseCallback) {
                        request.responseCallback(response);
                    }
                }
            } catch (err) {
                this.triggerError(
                    err as Error,
                    request.currentOperation,
                    errorType === "timeout",
                    request.message,
                    request.referer ?? ""
                );
            }
        });
    }

    private subscriptionReceived(response: GenericType): void {
        const id = this.state.subscriptionUuids[response.MessageId] ?? "";
        const subscription = this.state.subscriptions[id];

        if (!subscription) {
            this.error(
                "Subscription received: Cannot find subscription",
                id,
                response.MessageId
            );
            return;
        }

        try {
            if (response.Exception) {
                this.log("subscription errored", id);
                throw new Error(response.Exception);
            } else {
                this.log("subscription received", id);
                subscription.responseCallback(response);
            }
        } catch (err) {
            this.subscriptionError(id, err);
        } finally {
            subscription.finallyOnce();
        }
    }

    private removeSubscription(
        id: string,
        withSubscription?: (subscription: ISubscription) => void
    ): void {
        const subscription = this.state.subscriptions[id];

        if (!subscription) {
            return;
        }

        if (subscription.uuid) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.state.subscriptionUuids[subscription.uuid];
        }
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.state.subscriptions[id];
        withSubscription?.(subscription);
    }

    private subscriptionError(
        id: string,
        error: GenericType,
        type?: ErrorType
    ): void {
        this.removeSubscription(id, subscription => {
            const message = subscription.message;
            this.triggerError(
                error,
                `subscribing to ${subscription.currentOperation}`,
                type === "timeout",
                message,
                subscription.referer ?? ""
            );
        });
    }

    private cancelSubscription(id: number): void {
        const subscription = this.state.subscriptions[id];

        if (!subscription) {
            this.error("Cannot find subscription to unsubscribe", id);
            return;
        }

        if (subscription.uuid) {
            this.createRequest({
                status: "created",
                type: "unsubscribe",
                message: subscription.uuid as GenericType,
                responseCallback: _ => {
                    // do nothing with response
                },
                errorCallback: _ => {
                    // do nothing with error
                },
                currentOperation: "Unsubscribe from active uuid",
                finallyOnce: () => {
                    /* do nothing */
                }
            });
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.state.subscriptionUuids[subscription.uuid];
        }
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete this.state.subscriptions[id];

        this.log("cancel subscription", id, subscription.uuid);
    }

    private notifyStatusChange(): void {
        for (const cb of this.state.statusSubscriptions) {
            cb();
        }
    }

    private detectDuplicateMessages(
        message: RedDeer.Contracts.Base.Message | string
    ) {
        // Detect Duplicate Messages
        const json = JSON.stringify(message);
        const now = new Date();
        const recentMessage = this.state.recentMessages[json];
        if (recentMessage) {
            const timeDiff = now.getTime() - recentMessage.getTime();
            if (timeDiff < 100) {
                // eslint-disable-next-line no-console
                console.log("Duplicate Message (<100ms): " + json);
            }
        }
        this.state.recentMessages[json] = now;

        // Ignore 'unsubscribe' messages when in duplucate detection
        if (typeof message === "string") {
            return;
        }
        // Detect Duplicate Message Types
        const messageType = message.MessageType;
        if (messageType) {
            let recentMessageTypes = this.state.recentMessageTypes[messageType];
            if (!recentMessageTypes) {
                recentMessageTypes = [];
                this.state.recentMessageTypes[messageType] = recentMessageTypes;
            }
            recentMessageTypes.push(now);
            let recentMessageTypeCount = 0;
            for (const recentMessageType of recentMessageTypes) {
                const timeDiff = now.getTime() - recentMessageType.getTime();
                if (timeDiff < 100) {
                    recentMessageTypeCount++;
                }
            }
            if (recentMessageTypeCount >= 5) {
                // Disable for now
                // console.log("Duplicate Message Type (<100ms, Count: " + recentMessageTypeCount + "): " + messageType);
            }
        }
    }

    private cleanUpRecentMessages() {
        // Clean Up Recent Messages
        const jsonToDelete = [];
        const now = new Date();
        for (const json in this.state.recentMessages) {
            const recentMessage = this.state.recentMessages[json];
            const timeDiff = now.getTime() - (recentMessage?.getTime() ?? 0);
            if (timeDiff > 1000) {
                jsonToDelete.push(json);
            }
        }
        jsonToDelete.forEach(json => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.state.recentMessages[json];
        });

        // Clean Up Recent Message Types
        const messageTypeToDelete = [];
        for (const messageType in this.state.recentMessageTypes) {
            this.state.recentMessageTypes[messageType] =
                this.state.recentMessageTypes[messageType]?.filter(
                    recentMessage => {
                        const timeDiff =
                            now.getTime() - recentMessage.getTime();
                        return timeDiff < 1000;
                    }
                ) ?? [];
            if (this.state.recentMessageTypes[messageType].length === 0) {
                messageTypeToDelete.push(messageType);
            }
        }
        messageTypeToDelete.forEach(messageType => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete this.state.recentMessageTypes[messageType];
        });
    }

    private async startCheckingIfLoggedIn() {
        // if using dev server, don't auto logout
        if (windowAsAny().isRedDeerDevServer) {
            return;
        }

        // it's likely that the LegacyTimelineDataStore is the first service to ask for the MessageBus, thus causing a dependency injection loop
        // to fix this issue, we await a timeout so that the container.get request for LegacyTimelineDataStore doesn't happen synchronously
        await new Promise(r => setTimeout(r));

        const legacyTimelineDataStore = this.container.get(
            LegacyTimelineDataStore
        );

        await when(() => legacyTimelineDataStore.isReady);
        let intervalId: number | undefined = undefined;

        const checkIfLoggedIn = () =>
            fetch(`${locationPrepend()}/Security/LoginStatus`, {
                credentials: "same-origin"
            })
                .then(response => {
                    if (!response.ok) {
                        // we don't care if it fails, only if the user isn't logged in
                        return;
                    }
                    return response.json();
                })
                .then(loginStatus => {
                    const isLoggedOut = !loginStatus.IsLoggedIn;
                    if (
                        isLoggedOut ||
                        loginStatus.IsImpersonating !==
                            legacyTimelineDataStore.response?.UserDetails
                                .IsImpersonating
                    ) {
                        window.clearInterval(intervalId);
                        this.invalidateSession(!isLoggedOut);
                    }
                })
                .catch(() => {
                    // we don't care if it fails, only if the user isn't logged in
                });

        intervalId = window.setInterval(() => {
            void checkIfLoggedIn();
        }, 10000);
    }

    private backgroundInterval(): void {
        // request timeouts
        const timeouts: ITimedOutRequest[] = [];
        if (this.state.status === "connected") {
            for (const id in this.state.requests) {
                const request = this.state.requests[id];
                if (request?.status === "sent") {
                    if (this.hasRequestTimedOut(request)) {
                        if (request.timeoutCallback) {
                            this.removeRequest(id, _ =>
                                request.timeoutCallback?.()
                            );
                        } else {
                            timeouts.push({
                                id,
                                currentOperation: request.currentOperation
                            });
                        }
                    }
                }
            }
        }
        for (const cb of this.state.timeoutNoticeSubscriptions) {
            cb(timeouts);
        }

        // recent messages
        this.cleanUpRecentMessages();
    }

    private hasRequestTimedOut(request: IRequest): boolean {
        const baseTime = Math.max(
            request.lastSentTime ?? 0,
            this.state.lastConnectionTime ?? 0
        );
        const timeout = request.timeoutInMs ?? defaultTimeoutInMs;
        return Date.now() > baseTime + timeout;
    }

    private log(...args: GenericType[]): void {
        if (this.hasLogFeature()) {
            // eslint-disable-next-line no-console
            console.log(...args);
        }
    }

    private error(...args: GenericType[]): void {
        if (this.hasLogFeature()) {
            // eslint-disable-next-line no-console
            console.error(...args);
        }
    }

    private hasLogFeature(): boolean {
        try {
            const userPermissionService =
                windowAsAny().RedDeer?.UI?.App?.getInject?.(
                    "userPermissionService"
                ); // as UI.UserPermissionService;
            if (userPermissionService?.hasFeature("messageBusLog")) {
                return true;
            }
        } catch (e) {
            /* do nothing */
        }
        return false;
    }

    private triggerError(
        err: Error,
        operationName: string,
        isTimeout: boolean,
        message?: RedDeer.Contracts.Base.Message | string,
        requestUrl?: string
    ): void {
        window.ErrorHandler.reportError(err, operationName, undefined, {
            isTimeout,
            message: typeof message === "object" ? message : undefined,
            requestUrl
        });
    }

    private attachMessageId(
        requestMessage?: RedDeer.Contracts.Base.Message | string
    ) {
        if (typeof requestMessage !== "object") {
            return;
        }

        requestMessage.MessageId ??= uuidv4();
    }

    private attachReferer(request: IRequestReferer) {
        request.referer ??= window.location.href;
    }
}

function onceOnly(callback: () => void): () => void {
    let count = 0;

    return () => {
        count++;
        if (count === 1) {
            callback();
        }
    };
}
